import { Formik } from "formik";
import omit from "lodash/omit";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createContract } from "../../../../../../../../actions/employers/contractActions";
import { getTempPrerequisites } from "../../../../../../../../actions/employers/tempPrerequisiteActions";
import { setProfileDrawerVisible } from "../../../../../../../../actions/shared/uiActions";
import { getJobCast } from "../../../../../../../../selectors/jobcastSelectors";
import {
  buildFullName,
  mapKeysToSnakeCase,
} from "../../../../../../../../util/formatHelpers";
import { string, object, array } from "../../../../../../../../util/yup";
import useSnackbar from "../../../../../../../General/customHooks/useSnackbar";
import { mergePrequisites } from '../../../../../helpers';

function FormInitializer({ candidate, children }) {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const organizationPrerequisites = useSelector(getTempPrerequisites);
  const jobCast = useSelector((state) => getJobCast(state, candidate.attributes.jobcastId));
  const jobcastPrerequisites = jobCast.attributes.tempPrerequisites;

  const costCenters = useSelector((state) => state.costCenters.items);

  const initialValues = useMemo(() => {
    const {
      agencyName,
      agencyLogoUrl,
      recruiterFirstName,
      recruiterLastName,
      hourlyBillRate,
      availableStartDate,
    } = candidate.attributes;

    const costCenterIds = Object.keys(costCenters);

    return {
      // AGENCY INFORMATION (uneditable fields)
      agencyName,
      agencyLogoUrl,
      recruiterName: buildFullName(recruiterFirstName, recruiterLastName),
      hourlyBillRate,
      // WORK INFORMATION
      scheduledStartDate: availableStartDate,
      scheduledEndDate: null,
      primaryWorkState: null,
      primaryWorkZipCode: null,
      statementOfWorkDescription: jobCast.attributes.description.replace(/<img[^>]*>/g, ""),
      temp_prerequisites: mergePrequisites(jobcastPrerequisites, organizationPrerequisites),
      // BILLING INFORMATION
      costCenterId: costCenterIds.length === 1 ? costCenterIds[0] : null,
      invoiceMemo: null,
      timesheetApproverIds: jobCast.attributes.timesheetApproverIds || [],
    };
  }, [
    candidate.attributes,
    costCenters,
    jobcastPrerequisites,
    organizationPrerequisites,
    jobCast.attributes.timesheetApproverIds,
    jobCast.attributes.description
  ]);

  const validationSchema = useMemo(
    () =>
      object().shape({
        scheduledStartDate: string().nullable().required("Cannot be blank"),
        scheduledEndDate: string().nullable().required("Cannot be blank"),
        primaryWorkState: string().nullable().required("Cannot be blank"),
        primaryWorkZipCode: string()
          .nullable()
          .required("Cannot be blank")
          .matches(/^\d*$/, "Digits only")
          .min(5, "Must be 5 characters")
          .max(5, "Must be 5 characters"),
        statementOfWorkDescription: string()
          .nullable()
          .minWordsWithoutHTML(3, "Must be at least 3 words")
          .required("Cannot be blank"),
        costCenterId: string().nullable().required("Cannot be blank"),
        invoiceMemo: string().nullable().max(600, "Must be less than 600 characters"),
        timesheetApproverIds: array().of(string()).min(1, "Cannot be blank"),
      }),
    []
  );

  const onSubmit = useCallback(
    (values, { setSubmitting }) => {
      const submitValues = {
        ...mapKeysToSnakeCase(
          omit(values, [
            "agencyName",
            "agencyLogoUrl",
            "recruiterName",
            "hourlyBillRate",
          ])
        ),
        temp_prerequisites: values.temp_prerequisites.filter(prerequisite => prerequisite.checked),
        recruiter_submission_id: candidate.id,
      };

      return dispatch(createContract(submitValues))
        .then(() => {
          setSubmitting(false);
          dispatch(setProfileDrawerVisible(false));
          snackbar.showMessage(
            "Candidate successfully advanced",
            null,
            () => {},
            { duration: 3000 }
          );
          // open rate modal
        })
        .catch(() => {
          setSubmitting(false);
        });
    },
    [dispatch, candidate.id, snackbar]
  );

  return (
    <Formik {...{ initialValues, validationSchema, onSubmit }}>
      {children}
    </Formik>
  );
}

export default FormInitializer;
